import axios from 'axios';
import { withFreshAuth } from './freshAuth';
import { CancelledError } from '../lib/recognition/cancel';

const buildClient = (baseURL) => {
  const c = axios.create({ baseURL,
    headers: { 'Content-Type': 'application/json' } });
  c.interceptors.request.use(withFreshAuth);
  return c;
};

export const fetchService = {
  /**
   * Старт скачивания. `opts.diarize` — «по участникам»: bit-fetch отдаст
   * скачанное в bit-diarize-v3 вместо parakeet; число участников — точное
   * или вилка (lib/recognition/sources/file.js, speakerParams). `opts.language` —
   * язык речи ('ru' → v3, другой → parakeet); без него bit-fetch решает сам.
   */
  async submitUrl(baseUrl, _token, url, opts = {}) {
    const r = await buildClient(baseUrl).post('/fetch/url', {
      url,
      diarize: !!opts.diarize,
      remove_timestamps: true,
      num_speakers: opts.num_speakers ?? null,
      min_speakers: opts.min_speakers ?? null,
      max_speakers: opts.max_speakers ?? null,
      ...(opts.language ? { language: opts.language } : {}),
    });
    return r.data; // { fetch_id, title, duration }
  },

  /**
   * Отмена загрузки: освобождает слот и снимает скачивание.
   *
   * Ошибки глотаем — задача могла успеть закончиться или быть подчищенной по
   * TTL, и жаловаться человеку на неудачную отмену не за что.
   */
  async cancelFetch(baseUrl, fetchId) {
    if (!baseUrl || !fetchId) return null;
    try {
      const r = await buildClient(baseUrl).delete(`/fetch/${fetchId}`);
      return r.data;
    } catch (err) {
      return null;
    }
  },

  /**
   * Ждёт, пока bit-fetch скачает запись и отдаст её на распознавание, и
   * возвращает итоговый статус целиком: `task_id`, `engine` (кому ушёл файл —
   * `v3`, `parakeet` или `diarize`) и `duration`.
   *
   * 🔴 Длительность — отсюда, а не из ответа на старт: у облачного диска она
   * известна только после скачивания, и в Истории стояло «—».
   */
  async waitFetch(baseUrl, fetchId, { intervalMs = 3000, onProgress, signal } = {}) {
    if (!fetchId) throw new Error('Не получен идентификатор загрузки');
    const c = buildClient(baseUrl);
    for (;;) {
      if (signal?.aborted) throw new CancelledError();
      const { data } = await c.get(`/fetch/${fetchId}`);
      if (data.state === 'FORWARDED') return data;
      if (data.state === 'ERROR') {
        throw new Error(data.error || 'Ошибка скачивания по ссылке');
      }
      if (onProgress && typeof data.progress === 'number') onProgress(data.progress);
      await new Promise((res) => setTimeout(res, intervalMs));
    }
  },

  /** То же, но только task_id — для прежнего экрана (TranscriptionWithoutDiarization). */
  async pollFetch(baseUrl, _token, fetchId, opts = {}) {
    return (await fetchService.waitFetch(baseUrl, fetchId, opts)).task_id;
  },
};
